import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Search, 
  Filter, 
  Eye, 
  Download,
  ChevronLeft,
  ChevronRight,
  Music,
  User,
  Clock,
  Star
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { DateRangePicker } from '@/components/ui/date-picker';

export interface AnnotationRecord {
  id: string;
  sessionId: string;
  participantId: string;
  songId: string;
  songTitle: string;
  songSource: 'suno' | 'mureka' | 'sonauto' | 'udio' | 'human';
  authenticityAssessment: 'real' | 'ai-generated' | 'uncertain';
  familiarityLevel: 'know' | 'familiar' | 'never' | 'uncertain';
  aestheticQuality: number;
  playlistLikelihood: number;
  musicalCreativity: number;
  productionQuality: number;
  emotionalEngagement: number;
  aiAspects?: string[];
  moodTags: string[];
  aestheticComment?: string;
  songDescription?: string;
  annotationDuration: number; // in seconds
  songListeningTime: number; // in seconds
  songDuration?: number; // in seconds
  listeningPercentage?: number; // percentage (0-100+)
  createdAt: string;
  participantDemographics: {
    musicalEngagement: string;
    formalTrainingYears: number;
    age?: number;
    ageRange?: string;
  };
}

interface AnnotationFilters {
  search: string;
  source?: string;
  authenticity?: string;
  familiarity?: string;
  participantId?: string;
  dateRange?: 'today' | 'week' | 'month' | 'custom' | 'all';
  startDate?: Date;
  endDate?: Date;
  minRating?: number;
  maxRating?: number;
  qualityLevel?: 'high' | 'medium' | 'low';
}

interface AnnotationBrowserProps {
  onViewDetails?: (annotation: AnnotationRecord) => void;
  onExport?: (filters: AnnotationFilters) => void;
  className?: string;
}

interface QualityMetrics {
  completionRate: number;
  averageTime: number;
  consistencyScore: number;
  engagementLevel: 'high' | 'medium' | 'low';
}

// Mock data for demonstration
const mockAnnotations: AnnotationRecord[] = [
  {
    id: 'ann_001',
    sessionId: 'sess_001',
    participantId: 'part_001',
    songId: 'suno_001',
    songTitle: 'Electronic Dreams',
    songSource: 'suno',
    authenticityAssessment: 'ai-generated',
    familiarityLevel: 'never',
    aestheticQuality: 7,
    playlistLikelihood: 6,
    musicalCreativity: 8,
    productionQuality: 7,
    emotionalEngagement: 6,
    aiAspects: ['Singing voice', 'Rhythm'],
    moodTags: ['energetic', 'electronic', 'futuristic'],
    aestheticComment: 'Interesting sound design but vocals sound artificial',
    songDescription: 'Electronic track with synthetic vocals and complex rhythms',
    annotationDuration: 127,
    songListeningTime: 89,
    songDuration: 180,
    listeningPercentage: 49.4,
    createdAt: '2024-01-15T10:30:00Z',
    participantDemographics: {
      musicalEngagement: 'enthusiast',
      formalTrainingYears: 3,
      age: 28,
      ageRange: '25-34'
    }
  },
  {
    id: 'ann_002',
    sessionId: 'sess_002',
    participantId: 'part_002',
    songId: 'mureka_001',
    songTitle: 'Synthwave Nights',
    songSource: 'mureka',
    authenticityAssessment: 'real',
    familiarityLevel: 'familiar',
    aestheticQuality: 8,
    playlistLikelihood: 9,
    musicalCreativity: 7,
    productionQuality: 8,
    emotionalEngagement: 8,
    moodTags: ['nostalgic', 'dreamy', 'retro'],
    songDescription: 'Classic synthwave with great atmosphere',
    annotationDuration: 89,
    songListeningTime: 156,
    songDuration: 210,
    listeningPercentage: 74.3,
    createdAt: '2024-01-15T11:15:00Z',
    participantDemographics: {
      musicalEngagement: 'musician',
      formalTrainingYears: 8,
      ageRange: '35-44'
    }
  }
];

function AnnotationDetailsModal({ 
  annotation, 
  onClose 
}: { 
  annotation: AnnotationRecord; 
  onClose: () => void;
}) {
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRatingColor = (rating: number): string => {
    if (rating >= 8) return 'text-green-600';
    if (rating >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const assessAnnotationQuality = (annotation: AnnotationRecord): QualityMetrics => {
    // Calculate completion rate based on filled fields
    const totalFields = 8; // Core required fields
    let filledFields = 5; // Always have core ratings
    if (annotation.aestheticComment) filledFields++;
    if (annotation.songDescription) filledFields++;
    if (annotation.moodTags.length > 0) filledFields++;
    
    const completionRate = filledFields / totalFields;
    
    // Assess time spent (reasonable range: 30-300 seconds)
    const timeScore = annotation.annotationDuration >= 30 && annotation.annotationDuration <= 300 ? 1 : 0.5;
    
    // Check rating consistency (not all same values)
    const ratings = [
      annotation.aestheticQuality,
      annotation.playlistLikelihood,
      annotation.musicalCreativity,
      annotation.productionQuality,
      annotation.emotionalEngagement
    ];
    const uniqueRatings = new Set(ratings).size;
    const consistencyScore = uniqueRatings > 1 ? 1 : 0.3; // Penalize if all ratings are identical
    
    // Determine engagement level
    let engagementLevel: 'high' | 'medium' | 'low' = 'low';
    if (completionRate > 0.8 && timeScore === 1 && consistencyScore === 1) {
      engagementLevel = 'high';
    } else if (completionRate > 0.6 && (timeScore === 1 || consistencyScore === 1)) {
      engagementLevel = 'medium';
    }
    
    return {
      completionRate,
      averageTime: annotation.annotationDuration,
      consistencyScore,
      engagementLevel
    };
  };

  const quality = assessAnnotationQuality(annotation);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Music className="h-5 w-5" />
              Annotation Details
            </CardTitle>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Song Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">Song Information</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Title:</strong> {annotation.songTitle}</div>
                <div><strong>Source:</strong> <Badge variant="outline" className="capitalize">{annotation.songSource}</Badge></div>
                <div><strong>Song ID:</strong> <code className="text-xs bg-muted px-1 rounded">{annotation.songId}</code></div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Session Information</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Session ID:</strong> <code className="text-xs bg-muted px-1 rounded">{annotation.sessionId}</code></div>
                <div><strong>Participant ID:</strong> <code className="text-xs bg-muted px-1 rounded">{annotation.participantId}</code></div>
                <div><strong>Annotation Time:</strong> {formatDuration(annotation.annotationDuration)}</div>
                <div><strong>Listening Time:</strong> {formatDuration(annotation.songListeningTime)} {annotation.listeningPercentage && `(${annotation.listeningPercentage.toFixed(1)}%)`}</div>
                <div><strong>Created:</strong> {new Date(annotation.createdAt).toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Core Assessments */}
          <div>
            <h3 className="font-semibold mb-3">Core Assessments</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Authenticity Assessment</Label>
                <Badge 
                  variant={annotation.authenticityAssessment === 'ai-generated' ? 'destructive' : 
                          annotation.authenticityAssessment === 'real' ? 'default' : 'secondary'}
                  className="ml-2"
                >
                  {annotation.authenticityAssessment}
                </Badge>
              </div>
              <div>
                <Label>Familiarity Level</Label>
                <Badge variant="outline" className="ml-2 capitalize">
                  {annotation.familiarityLevel}
                </Badge>
              </div>
            </div>
          </div>

          {/* Rating Scales */}
          <div>
            <h3 className="font-semibold mb-3">Rating Scales (1-10)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center justify-between">
                <Label>Aesthetic Quality</Label>
                <div className={cn("flex items-center gap-2", getRatingColor(annotation.aestheticQuality))}>
                  <Star className="h-4 w-4" />
                  <span className="font-bold">{annotation.aestheticQuality}/10</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Playlist Likelihood</Label>
                <div className={cn("flex items-center gap-2", getRatingColor(annotation.playlistLikelihood))}>
                  <Star className="h-4 w-4" />
                  <span className="font-bold">{annotation.playlistLikelihood}/10</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Musical Creativity</Label>
                <div className={cn("flex items-center gap-2", getRatingColor(annotation.musicalCreativity))}>
                  <Star className="h-4 w-4" />
                  <span className="font-bold">{annotation.musicalCreativity}/10</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Production Quality</Label>
                <div className={cn("flex items-center gap-2", getRatingColor(annotation.productionQuality))}>
                  <Star className="h-4 w-4" />
                  <span className="font-bold">{annotation.productionQuality}/10</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Emotional Engagement</Label>
                <div className={cn("flex items-center gap-2", getRatingColor(annotation.emotionalEngagement))}>
                  <Star className="h-4 w-4" />
                  <span className="font-bold">{annotation.emotionalEngagement}/10</span>
                </div>
              </div>
            </div>
          </div>

          {/* AI Aspects */}
          {annotation.aiAspects && annotation.aiAspects.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">AI Aspects Identified</h3>
              <div className="flex flex-wrap gap-2">
                {annotation.aiAspects.map((aspect, index) => (
                  <Badge key={index} variant="destructive">
                    {aspect}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Mood Tags */}
          <div>
            <h3 className="font-semibold mb-2">Mood Tags</h3>
            <div className="flex flex-wrap gap-2">
              {annotation.moodTags.map((tag, index) => (
                <Badge key={index} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Comments */}
          {(annotation.aestheticComment || annotation.songDescription) && (
            <div className="space-y-4">
              <h3 className="font-semibold">Comments</h3>
              {annotation.aestheticComment && (
                <div>
                  <Label className="text-sm font-medium">Aesthetic Quality Comment:</Label>
                  <p className="text-sm text-muted-foreground mt-1 p-2 bg-muted rounded">
                    {annotation.aestheticComment}
                  </p>
                </div>
              )}
              {annotation.songDescription && (
                <div>
                  <Label className="text-sm font-medium">Song Description:</Label>
                  <p className="text-sm text-muted-foreground mt-1 p-2 bg-muted rounded">
                    {annotation.songDescription}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Data Quality Assessment */}
          <div>
            <h3 className="font-semibold mb-3">Data Quality Assessment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="text-center">
                <div className="text-lg font-bold text-primary">
                  {Math.round(quality.completionRate * 100)}%
                </div>
                <div className="text-xs text-muted-foreground">Completion Rate</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold">
                  {Math.round(quality.consistencyScore * 100)}%
                </div>
                <div className="text-xs text-muted-foreground">Consistency</div>
              </div>
              <div className="text-center">
                <Badge 
                  variant={quality.engagementLevel === 'high' ? 'default' : 
                          quality.engagementLevel === 'medium' ? 'secondary' : 'destructive'}
                >
                  {quality.engagementLevel} quality
                </Badge>
                <div className="text-xs text-muted-foreground mt-1">Overall Quality</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold">
                  {quality.averageTime >= 30 && quality.averageTime <= 300 ? '✓' : '⚠️'}
                </div>
                <div className="text-xs text-muted-foreground">Annotation Time</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">
                  {annotation.songListeningTime >= 15 ? '✓' : '⚠️'}
                </div>
                <div className="text-xs text-muted-foreground">Listening Time</div>
              </div>
            </div>
          </div>

          {/* Participant Demographics */}
          <div>
            <h3 className="font-semibold mb-2">Participant Demographics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <Label>Musical Engagement:</Label>
                <Badge variant="outline" className="ml-2 capitalize">
                  {annotation.participantDemographics.musicalEngagement}
                </Badge>
              </div>
              <div>
                <Label>Formal Training:</Label>
                <span className="ml-2">{annotation.participantDemographics.formalTrainingYears} years</span>
              </div>
              {annotation.participantDemographics.age && (
                <div>
                  <Label>Age:</Label>
                  <span className="ml-2">{annotation.participantDemographics.age}</span>
                </div>
              )}
              {annotation.participantDemographics.ageRange && (
                <div>
                  <Label>Age Range:</Label>
                  <span className="ml-2">{annotation.participantDemographics.ageRange}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function AnnotationBrowser({ onViewDetails, onExport, className }: AnnotationBrowserProps) {
  const [annotations, setAnnotations] = useState<AnnotationRecord[]>(mockAnnotations);
  const [filters, setFilters] = useState<AnnotationFilters>({ search: '' });
  const [selectedAnnotation, setSelectedAnnotation] = useState<AnnotationRecord | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [qualityMetrics, setQualityMetrics] = useState<QualityMetrics | null>(null);
  
  const itemsPerPage = 10;
  const totalPages = Math.ceil(annotations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnnotations = annotations.slice(startIndex, endIndex);

  const loadAnnotations = async () => {
    setIsLoading(true);
    try {
      // Use real API call with filters
      const { adminService } = await import('@/client/services/admin');
      const apiFilters = {
        ...(filters.search && { songId: filters.search }),
        ...(filters.source && { songSource: filters.source }),
        ...(filters.authenticity && { authenticityAssessment: filters.authenticity }),
        limit: 50, // Load more for better browsing
        offset: (currentPage - 1) * itemsPerPage
      };
      
      const response = await adminService.getAnnotations(apiFilters);
      setAnnotations(response.annotations);
    } catch (error) {
      console.error('Failed to load annotations:', error);
      // Fallback to mock data if API fails
      setAnnotations(mockAnnotations);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAnnotations();
  }, [filters]);

  const handleViewDetails = (annotation: AnnotationRecord) => {
    setSelectedAnnotation(annotation);
    onViewDetails?.(annotation);
  };

  const handleExport = () => {
    onExport?.(filters);
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAuthenticityBadge = (assessment: string) => {
    const variants = {
      'real': 'default',
      'ai-generated': 'destructive',
      'uncertain': 'secondary'
    } as const;
    
    return (
      <Badge variant={variants[assessment as keyof typeof variants] || 'outline'}>
        {assessment}
      </Badge>
    );
  };

  const assessAnnotationQuality = (annotation: AnnotationRecord): QualityMetrics => {
    // Calculate completion rate based on filled fields
    const totalFields = 8; // Core required fields
    let filledFields = 5; // Always have core ratings
    if (annotation.aestheticComment) filledFields++;
    if (annotation.songDescription) filledFields++;
    if (annotation.moodTags.length > 0) filledFields++;
    
    const completionRate = filledFields / totalFields;
    
    // Assess time spent (reasonable range: 30-300 seconds)
    const timeScore = annotation.annotationDuration >= 30 && annotation.annotationDuration <= 300 ? 1 : 0.5;
    
    // Check rating consistency (not all same values)
    const ratings = [
      annotation.aestheticQuality,
      annotation.playlistLikelihood,
      annotation.musicalCreativity,
      annotation.productionQuality,
      annotation.emotionalEngagement
    ];
    const uniqueRatings = new Set(ratings).size;
    const consistencyScore = uniqueRatings > 1 ? 1 : 0.3; // Penalize if all ratings are identical
    
    // Determine engagement level
    let engagementLevel: 'high' | 'medium' | 'low' = 'low';
    if (completionRate > 0.8 && timeScore === 1 && consistencyScore === 1) {
      engagementLevel = 'high';
    } else if (completionRate > 0.6 && (timeScore === 1 || consistencyScore === 1)) {
      engagementLevel = 'medium';
    }
    
    return {
      completionRate,
      averageTime: annotation.annotationDuration,
      consistencyScore,
      engagementLevel
    };
  };

  const getQualityBadge = (quality: QualityMetrics) => {
    const variants = {
      'high': 'default',
      'medium': 'secondary',
      'low': 'destructive'
    } as const;
    
    return (
      <Badge variant={variants[quality.engagementLevel]} className="text-xs">
        {quality.engagementLevel} quality
      </Badge>
    );
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Annotation Browser</h2>
          <p className="text-muted-foreground">
            View and analyze individual annotation records
          </p>
        </div>
        <Button onClick={handleExport} className="gap-2">
          <Download className="h-4 w-4" />
          Export Data
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Song title, participant..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-8"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="source">Source</Label>
              <Select
                value={filters.source || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, source: value === 'all' ? undefined : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="suno">Suno</SelectItem>
                  <SelectItem value="mureka">Mureka</SelectItem>
                  <SelectItem value="sonauto">Sonauto</SelectItem>
                  <SelectItem value="udio">Udio</SelectItem>
                  <SelectItem value="human">Human</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="authenticity">Authenticity</Label>
              <Select
                value={filters.authenticity || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, authenticity: value === 'all' ? undefined : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All assessments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All assessments</SelectItem>
                  <SelectItem value="real">Real</SelectItem>
                  <SelectItem value="ai-generated">AI-generated</SelectItem>
                  <SelectItem value="uncertain">Uncertain</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="familiarity">Familiarity</Label>
              <Select
                value={filters.familiarity || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, familiarity: value === 'all' ? undefined : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="know">Know this song</SelectItem>
                  <SelectItem value="familiar">Sounds familiar</SelectItem>
                  <SelectItem value="never">Never heard</SelectItem>
                  <SelectItem value="uncertain">Uncertain</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="dateRange">Date Range</Label>
              <Select
                value={filters.dateRange || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, dateRange: value === 'all' ? undefined : value as any }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This week</SelectItem>
                  <SelectItem value="month">This month</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="quality">Quality Level</Label>
              <Select
                value={filters.qualityLevel || 'all'}
                onValueChange={(value) =>
                  setFilters(prev => ({ ...prev, qualityLevel: value === 'all' ? undefined : value as any }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All quality levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All quality levels</SelectItem>
                  <SelectItem value="high">High quality</SelectItem>
                  <SelectItem value="medium">Medium quality</SelectItem>
                  <SelectItem value="low">Low quality</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Custom Date Range Picker */}
          {filters.dateRange === 'custom' && (
            <div className="mt-4">
              <Label>Custom Date Range</Label>
              <div className="mt-2 max-w-md">
                <DateRangePicker
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onStartDateSelect={(date) =>
                    setFilters(prev => ({ ...prev, startDate: date }))
                  }
                  onEndDateSelect={(date) =>
                    setFilters(prev => ({ ...prev, endDate: date }))
                  }
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quality Summary */}
      {annotations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Data Quality Assessment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {Math.round((annotations.filter(a => assessAnnotationQuality(a).engagementLevel === 'high').length / annotations.length) * 100)}%
                </div>
                <div className="text-sm text-muted-foreground">High Quality</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {Math.round(annotations.reduce((sum, a) => sum + assessAnnotationQuality(a).completionRate, 0) / annotations.length * 100)}%
                </div>
                <div className="text-sm text-muted-foreground">Avg Completion</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {Math.round(annotations.reduce((sum, a) => sum + a.annotationDuration, 0) / annotations.length)}s
                </div>
                <div className="text-sm text-muted-foreground">Avg Annotation Time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {Math.round(annotations.reduce((sum, a) => sum + a.songListeningTime, 0) / annotations.length)}s
                </div>
                <div className="text-sm text-muted-foreground">Avg Listening Time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">
                  {Math.round(annotations.reduce((sum, a) => sum + assessAnnotationQuality(a).consistencyScore, 0) / annotations.length * 100)}%
                </div>
                <div className="text-sm text-muted-foreground">Consistency</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Annotations ({annotations.length})</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Song</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Authenticity</TableHead>
                  <TableHead>Avg Rating</TableHead>
                  <TableHead>Quality</TableHead>
                  <TableHead>Annotation Time</TableHead>
                  <TableHead>Listening Time</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentAnnotations.map((annotation) => {
                  const avgRating = (
                    annotation.aestheticQuality +
                    annotation.playlistLikelihood +
                    annotation.musicalCreativity +
                    annotation.productionQuality +
                    annotation.emotionalEngagement
                  ) / 5;

                  const quality = assessAnnotationQuality(annotation);

                  return (
                    <TableRow key={annotation.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{annotation.songTitle}</div>
                          <div className="text-sm text-muted-foreground">
                            {annotation.songId}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {annotation.songSource}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getAuthenticityBadge(annotation.authenticityAssessment)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 text-yellow-500" />
                          {avgRating.toFixed(1)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getQualityBadge(quality)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {formatDuration(annotation.annotationDuration)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-blue-500" />
                          <div>
                            {formatDuration(annotation.songListeningTime)}
                            {annotation.listeningPercentage && (
                              <div className="text-xs text-muted-foreground">
                                {annotation.listeningPercentage.toFixed(1)}%
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(annotation.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(annotation)}
                          className="gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Modal */}
      {selectedAnnotation && (
        <AnnotationDetailsModal
          annotation={selectedAnnotation}
          onClose={() => setSelectedAnnotation(null)}
        />
      )}
    </div>
  );
}